import {
  COORDINATE_PRECISION,
  LATITUDE_MAX,
  LONGITUDE_MAX,
} from "@utils/geocoding-utils";
import { WorldMapView } from "@components/world-map/world-map-types";
import { QueryParams } from "@router/route-params";
import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { parseNumberParam } from "@utils/url-utils";

/** Returns whether the given coordinates and zoom describe a view the map can navigate to. */
function isViewWithinBounds(view: WorldMapView): boolean {
  return (
    Math.abs(view.lat) <= LATITUDE_MAX &&
    Math.abs(view.lng) <= LONGITUDE_MAX &&
    view.zoom >= 0
  );
}

/**
 * Reads the world map view (center and zoom) from the URL query params.
 * Returns undefined when any of the params are missing or out of range.
 */
export function parseMapViewFromParams(
  searchParams: URLSearchParams
): WorldMapView | undefined {
  const lat = parseNumberParam(searchParams, QueryParams.lat);
  const lng = parseNumberParam(searchParams, QueryParams.lng);
  const zoom = parseNumberParam(searchParams, QueryParams.zoom);

  if (lat === null || lng === null || zoom === null) {
    return undefined;
  }

  const view: WorldMapView = { lat, lng, zoom };
  return isViewWithinBounds(view) ? view : undefined;
}

/**
 * Writes the world map view into the given query params, preserving all other params.
 * Coordinates are rounded for compact, stable URLs.
 */
export function applyMapViewToParams(
  searchParams: URLSearchParams,
  view: WorldMapView
): URLSearchParams {
  const nextParams = new URLSearchParams(searchParams);
  nextParams.set(QueryParams.lat, view.lat.toFixed(COORDINATE_PRECISION));
  nextParams.set(QueryParams.lng, view.lng.toFixed(COORDINATE_PRECISION));
  nextParams.set(QueryParams.zoom, String(Math.round(view.zoom)));
  return nextParams;
}

interface UseProjectsMapDeepLinkReturn {
  /** Initial map view parsed from the URL on mount, or undefined when not present. */
  initialView: WorldMapView | undefined;

  /** Persists the current map view into the URL query params when the map stops moving. */
  onViewStable: (view: WorldMapView) => void;
}

/**
 * Connects the projects world map to the URL via a deep link.
 * On mount it reads the `lat`, `lng` and `zoom` query params to restore the previous view,
 * and it writes them back whenever the map comes to rest so the position can be shared or restored.
 */
export function useProjectsMapDeepLink(): UseProjectsMapDeepLinkReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  // Capture the view from the URL only once on mount. Subsequent param updates from
  // panning/zooming must not change the initial view and re-trigger map navigation.
  const [initialView] = useState(() => parseMapViewFromParams(searchParams));

  /**
   * Persists the current map view into the URL query params when the map stops moving.
   */
  const onViewStable = useCallback(
    (view: WorldMapView): void => {
      // `replace` keeps the browser history clean since this fires on every pan/zoom rest.
      setSearchParams((prev) => applyMapViewToParams(prev, view), {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Option name defined by react-router
        replace: true,
      });
    },
    [setSearchParams]
  );

  return { initialView, onViewStable };
}
